import { canReadDomain } from '@/api/core/authorization';
import {
  readEzremsTransitionCommand,
  submitEzremsTransition,
  type EzremsTransitionCommand,
  type EzremsTransitionStatus,
} from '@/api/core/ezrems-transition-command';
import {
  readEzremsTransitionPreview,
  type EzremsTransitionPreview,
} from '@/api/core/ezrems-transition-preview';
import {
  readAppliedMidtermSale,
  readRoomPricing,
  sameRoomPricingState,
  type RoomPricingRead,
} from '@/api/core/native-midterm-preparation';
import { readRoomSale } from '@/api/core/room-sales';
import type { ServiceClient } from '@/api/http/client';
import { isApiError } from '@/api/http/errors';
import { isUuid } from '@/lib/validation';
import {
  Button,
  ConfirmDialog,
  DatePicker,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import {
  beforeFirstDateBound,
  firstDateBound,
  firstDateBoundMessage,
} from './first-date-lower-bound';
import {
  firstDateRefusal,
  missingStatusText,
  transitionCommandMissing,
  transitionNotAccepted,
  transitionRefusalText,
} from './first-date-refusal';
import type { PropertyRuntime } from './property-queries';
interface Props {
  runtime: PropertyRuntime;
  generation: number;
  roomId: string;
  roomName?: string;
  units?: { id: string; name: string }[];
  onApplied?: () => Promise<void>;
  /** 객실 연결 컨텍스트의 슬롯 개별 연결 수. 없으면(서버 미제공·권한 없음) 미확인으로 보수적으로 안내한다. */
  unitBindingCount?: number;
  isNativeBlocked?: () => boolean;
  onPendingChange?: (pending: { roomId: string; roomName?: string } | null) => void;
}
interface Intent {
  namespace: string;
  generation: number;
  roomId: string;
  roomName?: string;
  actorId: string;
  preview: EzremsTransitionPreview;
  command: EzremsTransitionCommand;
  preservedPricing?: RoomPricingRead;
}
// Memory only: keyed child remounts and authorization changes cannot discard uncertain commands.
const retained = new WeakMap<ServiceClient, Intent>();
const access = ({ runtime, generation }: Props) => {
  const a = runtime.session.getSnapshot();
  return (
    a.generation === generation &&
    a.status === 'authorized' &&
    a.authorization !== null &&
    !a.deniedDomains.includes('booking') &&
    canReadDomain(a.authorization, 'booking', 32)
  );
};
export function pendingEzremsTransition(runtime: PropertyRuntime, generation: number) {
  const held = retained.get(runtime.client);
  if (!held) return null;
  return held.namespace === runtime.apiNamespace &&
    held.generation === generation &&
    access({ runtime, generation, roomId: held.roomId })
    ? { roomId: held.roomId, roomName: held.roomName }
    : { roomId: '' };
}
/** YYYY-MM-DD 형식이면서 실제 달력에 있는 날짜(예: 2026-02-31 제외)만 통과시킨다. */
const isoDate = (text: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false;
  const [y, m, d] = text.split('-').map(Number);
  const parsed = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
  return parsed.getFullYear() === y && parsed.getMonth() === (m ?? 1) - 1 && parsed.getDate() === d;
};
const labels = {
  UNKNOWN: '미확인',
  UNBOUND: '최초 연결 필요',
  OBSERVED_NO_FUTURE: '후속 계약 없음 확인',
  FUTURE_CONTRACT: '후속 계약 있음',
  DRAFT_CONTRACT: '후속 계약 작성 중',
};
const product = (v: string | null) =>
  v === 'midterm' ? '미드텀' : v === 'long_term' ? '일반 판매' : '미설정';
const eligible = (d: EzremsTransitionPreview, c: EzremsTransitionCommand) =>
  d.write_supported &&
  !d.pending &&
  (c.target === 'GENERAL' ||
    (d.eligible &&
      d.contract_status !== 'UNKNOWN' &&
      !!c.first_move_in_date &&
      !beforeFirstDateBound(firstDateBound(d), c.first_move_in_date)));
export function EzremsTransitionPreviewSection(props: Props) {
  const auth = useSyncExternalStore(
    props.runtime.session.subscribe,
    props.runtime.session.getSnapshot,
    props.runtime.session.getSnapshot,
  );
  const key = JSON.stringify([
    props.runtime.apiNamespace,
    props.generation,
    props.roomId,
    props.units,
    auth,
  ]);
  return <Preview key={key} {...props} />;
}
function Preview(props: Props) {
  const { runtime, generation, onPendingChange } = props;
  const [data, setData] = useState<EzremsTransitionPreview | null>(null);
  const [held, setHeld] = useState<Intent | null>(() => retained.get(props.runtime.client) ?? null);
  useEffect(() => {
    onPendingChange?.(pendingEzremsTransition(runtime, generation));
  }, [held, onPendingChange, runtime, generation]);
  const [reviewed, setReviewed] = useState<Intent | null>(null);
  const [status, setStatus] = useState<EzremsTransitionStatus | null>(null);
  const [target, setTarget] = useState<'MIDTERM' | 'GENERAL'>('MIDTERM');
  const [date, setDate] = useState('');
  // 달력(DatePicker)은 방향키 이동을 지원하지 않아 키보드만으로는 먼 날짜를 고를 수 없다 — 같은 날짜 상태를
  // YYYY-MM-DD 직접 입력(Input)으로도 고를 수 있게 한다. 입력 중인 글자는 따로 두고, 올바른 날짜가 될 때만 date에 반영한다.
  const [dateText, setDateText] = useState('');
  const dateHintId = useId();
  const [dateTextTouched, setDateTextTouched] = useState(false);
  // 입력 중(미완성)에는 알리지 않고, 입력칸을 벗어나거나 열 글자를 다 쳤는데 올바르지 않을 때만 오류로 알린다.
  // 잘못된 글자는 입력 즉시 날짜로 받지 않으므로 검토 버튼은 계속 막혀 있다.
  const dateTextInvalid =
    dateText !== '' && !isoDate(dateText) && (dateTextTouched || dateText.length >= 10);
  useEffect(() => {
    // 달력에서 고르거나 초기화되면 입력 칸도 맞춘다(입력 중인 미완성 글자는 덮지 않는다).
    if (date !== '' && date !== dateText) setDateText(date);
    if (date === '' && isoDate(dateText)) setDateText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- date가 바뀔 때만 입력 칸을 맞춘다.
  }, [date]);
  const [dailyPrice, setDailyPrice] = useState('');
  const [replacePrice, setReplacePrice] = useState(false);
  const priceValid = /^[1-9]\d*$/.test(dailyPrice) && Number.isSafeInteger(Number(dailyPrice));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const alive = useRef(true);
  const lock = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const nativeBlocked = useRef(props.isNativeBlocked);
  nativeBlocked.current = props.isNativeBlocked;
  const valid = () => alive.current && access(props);
  const scope = (d: EzremsTransitionPreview) => {
    if (
      props.units &&
      JSON.stringify([...props.units.map((u) => u.id)].sort()) !==
        JSON.stringify([...d.unit_ids].sort())
    )
      throw new Error('Room units changed');
  };
  const actor = async () => {
    const a = await props.runtime.client.get<{ user_id?: unknown }>('users/me', {
      headers: { 'Cache-Control': 'no-store' },
    });
    if (!valid() || !isUuid(a.user_id)) throw new Error('Actor unavailable');
    return a.user_id;
  };
  const fail = (e: unknown, text = '계약·전환 준비를 확인하지 못했습니다.') => {
    if (!alive.current) return;
    setFeedback(text);
    if (isApiError(e) && e.status === 403)
      props.runtime.session.reportDomainAccessDenied('booking', props.generation);
  };
  const read = async () => {
    if (!valid() || lock.current) return;
    lock.current = true;
    setBusy(true);
    setData(null);
    setReviewed(null);
    setFeedback('');
    try {
      const d = await readEzremsTransitionPreview(props.runtime.client, props.roomId);
      scope(d);
      if (valid()) setData(d);
    } catch (e) {
      fail(e);
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const review = async () => {
    if (!valid() || lock.current || held || !data || nativeBlocked.current?.()) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    try {
      const actorId = await actor();
      const fresh = await readEzremsTransitionPreview(props.runtime.client, props.roomId);
      scope(fresh);
      if (!valid() || (await actor()) !== actorId) throw new Error('Actor changed');
      if (JSON.stringify(fresh) !== JSON.stringify(data)) {
        setData(fresh);
        throw new Error('Preview changed');
      }
      const command: EzremsTransitionCommand = {
        command_id: crypto.randomUUID(),
        target,
        expected_sale_revision: fresh.sale_revision,
        first_move_in_date: target === 'MIDTERM' ? date : null,
        ...(target === 'MIDTERM' && replacePrice ? { daily_price_minor: Number(dailyPrice) } : {}),
        reason,
      };
      if ((target === 'MIDTERM' && replacePrice && !priceValid) || !eligible(fresh, command))
        throw new Error('Unavailable');
      const preservedPricing =
        target === 'MIDTERM' && !replacePrice
          ? await readRoomPricing(props.runtime.client, props.roomId)
          : undefined;
      if (preservedPricing && (!valid() || (await actor()) !== actorId))
        throw new Error('Actor changed');
      if (nativeBlocked.current?.()) return;
      setReviewed({
        preservedPricing,
        namespace: props.runtime.apiNamespace,
        generation: props.generation,
        roomId: props.roomId,
        roomName: props.roomName,
        actorId,
        preview: fresh,
        command,
      });
    } catch (e) {
      fail(e);
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const owns = (i: Intent) =>
    valid() &&
    i.namespace === props.runtime.apiNamespace &&
    i.generation === props.generation &&
    i.roomId === props.roomId;
  const run = async (i: Intent, inspect = false) => {
    const recovering = retained.get(props.runtime.client) === i;
    if (!owns(i) || lock.current || (!inspect && !recovering && nativeBlocked.current?.())) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    setStatus(null);
    // 404를 "원명령 없음"으로 읽는 것은 상태 조회 호출 자체에 한정한다(뒤따른 readback의 404와 구분).
    const statusNotFound = { value: false };
    try {
      if ((await actor()) !== i.actorId) throw new Error('Actor changed');
      if (inspect) {
        const result = await readEzremsTransitionCommand(
          props.runtime.client,
          i.roomId,
          i.command.command_id,
          i.command.target,
        ).catch((statusError: unknown) => {
          statusNotFound.value = transitionCommandMissing(statusError);
          throw statusError;
        });
        if ((await actor()) !== i.actorId || !owns(i)) throw new Error('Actor changed');
        if ((result.outcome === 'APPLIED' || result.outcome === 'NOT_APPLIED') && !result.pending) {
          const fresh = await readEzremsTransitionPreview(props.runtime.client, i.roomId);
          scope(fresh);
          if ((await actor()) !== i.actorId || !owns(i)) throw new Error('Actor changed');
          if (
            fresh.pending ||
            fresh.sale_revision !== result.current_sale_revision ||
            retained.get(props.runtime.client) !== i
          )
            throw new Error('Current sale changed');
          if (result.outcome === 'APPLIED') {
            if (
              result.readback_matches !== true ||
              fresh.sale_product !== (i.command.target === 'MIDTERM' ? 'midterm' : 'long_term') ||
              fresh.provider_kind !== (i.command.target === 'MIDTERM' ? '미드텀' : '일반')
            )
              throw new Error('Transition readback mismatch');
            if (i.command.target === 'MIDTERM' && i.command.daily_price_minor !== undefined) {
              await readAppliedMidtermSale(props.runtime.client, i.roomId, {
                daily_price_minor: i.command.daily_price_minor,
                first_move_in_date: i.command.first_move_in_date ?? '',
                revision: result.current_sale_revision,
              });
            } else {
              const sale = await readRoomSale(props.runtime.client, i.roomId);
              if (
                sale.product !== (i.command.target === 'MIDTERM' ? 'midterm' : 'long_term') ||
                (i.command.target === 'MIDTERM' &&
                  sale.first_move_in_date !== i.command.first_move_in_date) ||
                sale.revision !== result.current_sale_revision ||
                sale.pending_outcome !== null
              )
                throw new Error('Sale readback mismatch');
            }
            if (i.preservedPricing) {
              const pricing = await readRoomPricing(props.runtime.client, i.roomId);
              if (!sameRoomPricingState(i.preservedPricing, pricing))
                throw new Error('Preserved pricing changed');
            }
            if ((await actor()) !== i.actorId || !owns(i)) throw new Error('Actor changed');
            await props.onApplied?.();
            if ((await actor()) !== i.actorId || !owns(i)) throw new Error('Actor changed');
          }
          retained.delete(props.runtime.client);
          setHeld(null);
          setData(fresh);
          setReviewed(null);
        }
        setStatus(result);
      } else {
        const fresh = await readEzremsTransitionPreview(props.runtime.client, i.roomId);
        scope(fresh);
        if ((await actor()) !== i.actorId || !owns(i)) throw new Error('Actor changed');
        const replay =
          retained.get(props.runtime.client)?.command.command_id === i.command.command_id;
        if (
          !replay &&
          (JSON.stringify(fresh) !== JSON.stringify(i.preview) || !eligible(fresh, i.command))
        ) {
          setData(fresh);
          throw new Error('Changed before submit');
        }
        if (!replay && nativeBlocked.current?.()) return;
        retained.set(props.runtime.client, i);
        setHeld(i);
        setReviewed(null);
        await submitEzremsTransition(props.runtime.client, i.roomId, i.command);
        if (owns(i))
          setFeedback('판매 요청 응답을 확인했습니다. 최종 반영은 판매 요청 상태를 조회하세요.');
      }
    } catch (e) {
      // 접수 전 거절(409 제외 4xx)이면 보존을 풀고, 409·그 외는 명령이 남았을 수 있어 원 요청을 보존한 채 둔다.
      // 상태 조회 404도 미실행의 증거가 아니라(대상 불일치·commit 전 경합 가능) 보존하며, 읽기로만 현재 상태를 다시 맞춘다.
      const fresh = !inspect && !recovering;
      const released = fresh && transitionNotAccepted(e);
      if (released && retained.get(props.runtime.client) === i) {
        retained.delete(props.runtime.client);
        setHeld(null);
      }
      if (inspect && statusNotFound.value) {
        let state: Parameters<typeof missingStatusText>[0] = { reread: false };
        try {
          const latest = await readEzremsTransitionPreview(props.runtime.client, i.roomId);
          scope(latest);
          if ((await actor()) === i.actorId && owns(i)) {
            setData(latest);
            state = {
              reread: true,
              trace:
                !latest.pending && latest.sale_revision === i.command.expected_sale_revision
                  ? 'none'
                  : 'changed',
            };
          }
        } catch (readError) {
          if (isApiError(readError) && readError.status === 403)
            props.runtime.session.reportDomainAccessDenied('booking', props.generation);
        }
        if (alive.current) setFeedback(missingStatusText(state));
        return;
      }
      const refusal = fresh ? firstDateRefusal(e) : null;
      fail(
        e,
        released
          ? '서버가 요청을 거절했습니다. 입력과 권한을 확인하세요.'
          : refusal === null
            ? undefined
            : transitionRefusalText(refusal, { scope: 'room', context: 'held' }),
      );
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const allowed = access(props);
  const ownHeld =
    held?.namespace === props.runtime.apiNamespace &&
    held.generation === props.generation &&
    held.roomId === props.roomId;
  const nativeIsBlocked = props.isNativeBlocked?.() ?? false;
  const blocked = busy || !!held || !!reviewed || nativeIsBlocked;
  const blockedReason = (() => {
    if (nativeIsBlocked) return '진행 중이거나 결과가 불확실한 Core 요청을 먼저 확인하세요.';
    if (!data) return '계약·전환 준비를 먼저 조회하세요.';
    if (!data.write_supported)
      return 'EZREMS 전환을 지원하지 않는 대상입니다. 연결 또는 지원 상태를 확인하세요.';
    if (data.pending) return '이전 판매 요청의 결과를 먼저 확인하세요.';
    if (target === 'MIDTERM') {
      if (data.contract_status === 'UNKNOWN')
        return '계약 상태를 확인해야 중기 판매로 전환할 수 있습니다.';
      if (!data.eligible) return '계약·준비일 조건을 충족하지 못했습니다.';
      if (!date) return '입주 가능 첫날을 선택하세요.';
      if (beforeFirstDateBound(firstDateBound(data), date))
        return '선택한 날짜가 너무 이릅니다. 위 안내의 가장 이른 날부터 선택할 수 있습니다.';
      if (replacePrice && !priceValid) return '새 일가격을 양의 정수 원 단위로 입력하세요.';
    }
    if (!reason.trim()) return '전환 사유를 입력하세요.';
    return '전환 내용을 검토할 수 있습니다.';
  })();
  return (
    <section
      aria-label="현재 계약과 외부 전환 준비"
      className="body-sm space-y-4 rounded-md border p-4 leading-relaxed"
    >
      <h4 className="font-semibold">EZREMS 계약 타입 전환</h4>
      <p>실제 예약 가능 기간에는 준비 기간 3영업일이 반영됩니다.</p>
      <p>
        계약을 확인하고 중기 판매 또는 일반 판매 복귀를 검토합니다. Core 단독 판매 설정은 외부 계약
        타입을 변경하지 않습니다.
      </p>
      <Button
        className="h-auto min-h-8 whitespace-normal"
        variant="outline"
        disabled={!allowed || busy}
        onClick={() => void read()}
      >
        {feedback && !data && !held ? '다시 조회' : '계약·전환 준비 조회'}
      </Button>
      {!allowed && <p>계약·전환 준비 조회에 Booking 판매 권한이 필요합니다.</p>}
      {!data && (
        <p role="status">
          {busy ? '계약·전환 준비 조회 중' : '현재·후속 계약 및 입주 가능 첫날: 미확인'}
        </p>
      )}
      {data && (
        <>
          <div role="status" className="space-y-2">
            <p>계약 상태: {labels[data.contract_status]}</p>
            <p>계약·준비일 판단: {data.eligible ? '충족' : '미충족'}</p>
            <p>
              최종 계약 종료일: {data.current_end ?? '미확인'} · 실제 예약 가능 첫날:{' '}
              {data.earliest_date ?? '미확인'}
            </p>
            {data.write_unavailable_reason === 'UNBOUND' && (
              <p>
                {(props.unitBindingCount ?? 0) > 0
                  ? `이 객실은 슬롯 ${String(props.unitBindingCount)}개가 따로 연결되어 있어 객실 전체 전환은 쓸 수 없습니다. 슬롯 전환 화면에서 확인하세요.`
                  : props.unitBindingCount === 0
                    ? '이 객실은 아직 EZREMS 호실에 연결되지 않았습니다. 위에서 연결한 뒤 판매 전환을 검토하세요.'
                    : '이 객실은 객실 단위로 EZREMS에 연결되어 있지 않습니다. 객실 연결 또는 슬롯 연결 상태를 확인하세요.'}
              </p>
            )}
            {data.write_unavailable_reason === 'UNSUPPORTED_TARGET' && (
              <p>이 객실은 EZREMS 판매 전환을 지원하지 않습니다.</p>
            )}
            <p>
              미해결 판매 요청: {data.pending ? '있음' : '없음'} · 현재 판매:{' '}
              {product(data.sale_product)} · 전체 호실 {data.unit_ids.length}개
            </p>
          </div>
          <Select
            value={target}
            disabled={blocked}
            onValueChange={(v) => {
              if (v === 'MIDTERM' || v === 'GENERAL') setTarget(v);
            }}
            items={[
              { value: 'MIDTERM', label: '미드텀 판매' },
              { value: 'GENERAL', label: '일반 판매 복귀' },
            ]}
          >
            <SelectTrigger aria-label="외부 판매 대상" className="w-full sm:max-w-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MIDTERM">미드텀 판매</SelectItem>
              <SelectItem value="GENERAL">일반 판매 복귀</SelectItem>
            </SelectContent>
          </Select>
          {target === 'MIDTERM' && (
            <fieldset disabled={blocked} className="grid gap-3 sm:grid-cols-2">
              <legend className="mb-3 font-medium">미드텀 전환 설정</legend>
              <label className="flex items-center gap-2 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={replacePrice}
                  onChange={(event) => {
                    setReplacePrice(event.target.checked);
                  }}
                />
                전환과 함께 새 일가격 설정 (보증금·청소비 0원)
              </label>
              <label className="grid gap-1">
                전환 일가격 (원)
                <Input
                  aria-label="전환 일가격 (원)"
                  disabled={!replacePrice}
                  inputMode="numeric"
                  value={dailyPrice}
                  onChange={(e) => {
                    setDailyPrice(e.target.value);
                  }}
                />
              </label>
              <div className="grid gap-1">
                <p>입주 가능 첫날</p>
                <DatePicker
                  value={date ? new Date(`${date}T00:00:00`) : undefined}
                  onChange={(value) => {
                    setDate(
                      value
                        ? `${String(value.getFullYear())}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
                        : '',
                    );
                  }}
                />
                <Input
                  aria-label="입주 가능 첫날 직접 입력 (YYYY-MM-DD)"
                  aria-invalid={dateTextInvalid}
                  aria-describedby={dateHintId}
                  inputMode="numeric"
                  placeholder="YYYY-MM-DD"
                  value={dateText}
                  maxLength={10}
                  onChange={(e) => {
                    const text = e.target.value;
                    setDateText(text);
                    setDate(isoDate(text) ? text : '');
                  }}
                  onBlur={() => {
                    setDateTextTouched(true);
                  }}
                  onFocus={() => {
                    setDateTextTouched(false);
                  }}
                />
                <p
                  id={dateHintId}
                  role={dateTextInvalid ? 'alert' : undefined}
                  className="caption text-muted-foreground"
                >
                  {dateTextInvalid
                    ? '날짜는 YYYY-MM-DD 형식의 올바른 날짜로 입력하세요.'
                    : '달력 대신 키보드로 날짜를 입력할 수 있습니다. 달력에서 고른 날짜와 같은 값입니다.'}
                </p>
              </div>
              <p className="text-muted-foreground sm:col-span-2">
                {firstDateBoundMessage(firstDateBound(data))}
              </p>
              <p className="text-muted-foreground sm:col-span-2">
                {replacePrice
                  ? '선택 객실의 일가격을 새로 설정하고 보증금·청소비를 0원으로 적용합니다.'
                  : '현재 일가격·보증금·청소비·할인 설정을 유지하며 판매만 전환합니다.'}
              </p>
            </fieldset>
          )}
          <label className="grid gap-1">
            전환 사유
            <Input
              aria-label="전환 사유"
              value={reason}
              disabled={blocked}
              maxLength={500}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </label>
          {!held && (
            <>
              <p id="ezrems-transition-block" className="body-sm" role="status">
                {blockedReason}
              </p>
              <Button
                aria-describedby="ezrems-transition-block"
                className="h-auto min-h-8 whitespace-normal"
                disabled={
                  !allowed ||
                  blocked ||
                  (target === 'MIDTERM' && replacePrice && !priceValid) ||
                  !reason.trim() ||
                  Array.from(reason).length > 500 ||
                  !eligible(data, { target, first_move_in_date: date } as EzremsTransitionCommand)
                }
                onClick={() => void review()}
              >
                전환 접수 검토
              </Button>
            </>
          )}
        </>
      )}
      <ConfirmDialog
        open={!!reviewed && !nativeIsBlocked}
        onOpenChange={(open) => {
          if (!open) setReviewed(null);
        }}
        target={`${props.roomName ?? '객실 이름 미확인'} · 전체 호실 ${String(reviewed?.preview.unit_ids.length ?? 0)}개 (${props.units?.map((u) => u.name).join(', ') ?? '호실 이름 미확인'})`}
        action="전환 접수"
        consequence={`${reviewed?.command.target === 'GENERAL' ? '일반 판매 복귀' : '미드텀 판매'} · 입주 가능 첫날 ${reviewed?.command.first_move_in_date ?? '해당 없음'} · ${reviewed?.command.daily_price_minor ? `새 일가격 ${reviewed.command.daily_price_minor.toLocaleString('ko-KR')}원/일 · 보증금 0원 · 청소비 0원` : '일가격·보증금·청소비·할인 설정 유지'} · 사유 ${reviewed?.command.reason ?? ''}. 기존 예약·청구·결제 진행·출입 권한은 유지됩니다. 신규 미드텀 접수 가능 여부에 영향을 줍니다.`}
        variant="default"
        onConfirm={() => {
          if (reviewed) void run(reviewed);
        }}
      />
      {held && (
        <>
          <p role="alert">
            원래 판매 요청을 보존했습니다. 새 요청은 차단하고 자동 재전송하지 않습니다.
            {held.namespace === props.runtime.apiNamespace &&
              held.generation === props.generation &&
              allowed && (
                <>
                  {' '}
                  대상: {held.roomName ?? held.roomId} · 사유: {held.command.reason}.
                </>
              )}
          </p>
          {ownHeld && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                className="h-auto min-h-8 whitespace-normal"
                disabled={!allowed || busy}
                onClick={() => void run(held)}
              >
                같은 요청 다시 시도
              </Button>
              <Button
                className="h-auto min-h-8 whitespace-normal"
                disabled={!allowed || busy}
                onClick={() => void run(held, true)}
              >
                판매 요청 상태 조회
              </Button>
            </div>
          )}
        </>
      )}
      {status && (
        <p role="status">
          판매 요청 결과:{' '}
          {
            {
              PENDING: '확인 대기',
              UNKNOWN: '미확인',
              APPLIED: status.pending ? '확인 대기' : '반영 확인',
              NOT_APPLIED: '미반영 확인',
            }[status.outcome]
          }{' '}
          · 전송: {status.transport === 'DELIVERED' ? '전달 확인' : '미확인'} · 대상 상태 비교:{' '}
          {status.readback_matches === null
            ? '미확인'
            : status.readback_matches
              ? '일치'
              : '불일치'}{' '}
          ·{' '}
          {
            {
              AWAITING_RESULT: '결과 대기',
              TRANSPORT_UNKNOWN: '전송 여부 미확인',
              READBACK_UNAVAILABLE: '현재 상태 조회 불가',
              READBACK_MISMATCH: '현재 상태 불일치',
              FINALITY_UNCONFIRMED: '최종 반영 미확인',
            }[status.reason ?? 'FINALITY_UNCONFIRMED']
          }
        </p>
      )}
      {feedback && (
        <p role="status">
          {!held && !data ? '계약·전환 준비를 불러오지 못했습니다. 다시 조회해 주세요.' : feedback}
        </p>
      )}
      {(held !== null || status !== null) && (
        <p>
          접수 응답이나 대상 상태 일치만으로는 외부 최종 성공을 확인할 수 없습니다. 판매 요청 결과를
          확인하세요.
        </p>
      )}
    </section>
  );
}
